import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChefHat, 
  Utensils, 
  BarChart3, 
  MessageSquare, 
  Calendar, 
  CreditCard,
  Sparkles,
  TrendingUp,
  Globe,
  Zap,
  ArrowRight,
  Check,
  Percent,
  TrendingDown,
  PiggyBank,
  Ban,
  MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';

const features = [
  {
    icon: Utensils,
    title: 'Online Bestellen',
    description: 'Accepteer bezorg-, afhaal- en ter plaatse bestellingen via je eigen webshop.',
  },
  {
    icon: ChefHat,
    title: 'AI Menu Bouwer',
    description: 'Genereer aantrekkelijke beschrijvingen en slimme upsells met AI.',
  },
  {
    icon: BarChart3,
    title: 'Vraagvoorspelling',
    description: 'Voorspel drukke uren en populaire items met AI-analyses.',
  },
  {
    icon: MessageSquare,
    title: 'AI Chatbot',
    description: 'Help klanten met bestellen en beantwoord vragen 24/7.',
  },
  {
    icon: Calendar,
    title: 'Tafelreserveringen',
    description: 'Beheer boekingen en optimaliseer tafelomzet moeiteloos.',
  },
  {
    icon: CreditCard,
    title: 'Betalingsverwerking',
    description: 'Veilige Stripe-betalingen met fooien en bezorgkosten.',
  },
];

const aiFeatures = [
  'AI-gegenereerde menubeschrijvingen',
  'Slimme prijsaanbevelingen',
  'Vraagvoorspelling & analyses',
  'Klantgedrag inzichten',
  'Automatische upsell suggesties',
  'Meertalige ondersteuning',
];

const WHATSAPP_NUMBER = '32489234294';
const buildWhatsAppUrl = (msg) => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
const DEFAULT_WA_MESSAGE = 'Hoi! Ik heb een restaurant en wil graag meer info over FoodFlows online bestellen.';

export const LandingPage = () => {
  const [leadName, setLeadName] = useState('');
  const [leadRestaurant, setLeadRestaurant] = useState('');
  const [leadCity, setLeadCity] = useState('');

  const handleLeadSubmit = (e) => {
    e.preventDefault();
    const msg = `Hoi! Ik ben ${leadName} van ${leadRestaurant} in ${leadCity}. Ik wil graag een gratis demo van FoodFlows voor online bestellingen.`;
    window.open(buildWhatsAppUrl(msg), '_blank');
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Navigation */}
      <nav className="glass-nav fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <div className="w-10 h-10 bg-[#1A4D2E] rounded-xl flex items-center justify-center">
                <Utensils className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-xl text-[#1A4D2E]">FoodFlows</span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <a href="#zero-commission" className="text-[#E07A5F] hover:text-[#c25a3f] transition-colors font-semibold">0% Commissie</a>
              <a href="#features" className="text-gray-600 hover:text-[#1A4D2E] transition-colors font-medium">Functies</a>
              <a href="#demo-aanvragen" className="text-gray-600 hover:text-[#1A4D2E] transition-colors font-medium">Demo Aanvragen</a>
              <Link to="/shop/golden-fork-demo" className="text-gray-600 hover:text-[#1A4D2E] transition-colors font-medium">Demo Winkel</Link>
              <Link to="/blog" className="text-gray-600 hover:text-[#1A4D2E] transition-colors font-medium" data-testid="nav-blog">Blog</Link>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-[#1A4D2E] hover:bg-[#D8F3DC]" data-testid="login-btn">
                  Inloggen
                </Button>
              </Link>
              <Link to="/register" className="hidden md:block text-sm text-gray-500 hover:text-[#1A4D2E] transition-colors" data-testid="nav-register-link">
                Registreren
              </Link>
              <a href="#demo-aanvragen">
                <Button className="bg-[#25D366] hover:bg-[#1eb855] text-white rounded-full px-6" data-testid="nav-whatsapp-btn">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Demo Aanvragen
                </Button>
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#D8F3DC]/30 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slideUp">
              <div className="inline-flex items-center gap-2 bg-[#D8F3DC] text-[#1A4D2E] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                AI-Aangedreven Restaurant Platform
              </div>
              <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-[#1F2937] leading-tight tracking-tight mb-6">
                Jouw Restaurant,{' '}
                <span className="text-[#1A4D2E]">Aangedreven door AI</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8 max-w-xl">
                Lanceer je eigen online bestelwebshop in enkele minuten. Accepteer bestellingen, beheer tafels en laat je bedrijf groeien met AI-inzichten.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#demo-aanvragen">
                  <Button className="bg-[#25D366] hover:bg-[#1eb855] text-white rounded-full text-lg h-14 px-8 shadow-lg" data-testid="hero-cta-btn">
                    <MessageCircle className="mr-2 w-5 h-5" />
                    Vraag Gratis Demo Aan
                  </Button>
                </a>
                <Link to="/shop/golden-fork-demo">
                  <Button variant="outline" className="border-2 border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white rounded-full h-14 px-8 text-lg" data-testid="demo-btn">
                    Bekijk Demo Winkel
                  </Button>
                </Link>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Gratis demo & persoonlijk advies
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Opgezet in 5 minuten
                </div>
              </div>
            </div>
            <div className="relative animate-fadeIn hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.pexels.com/photos/7133566/pexels-photo-7133566.jpeg" 
                  alt="Gastronomisch eten"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">De Gouden Vork</p>
                        <p className="text-sm text-gray-500">3 nieuwe bestellingen • €127,50 vandaag</p>
                      </div>
                      <div className="flex items-center gap-1 text-green-600 bg-green-50 px-3 py-1 rounded-full">
                        <TrendingUp className="w-4 h-4" />
                        <span className="font-medium text-sm">+24%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === 0% COMMISSIE BANNER (tijdelijke promo) === */}
      <section id="zero-commission" className="relative py-20 bg-gradient-to-br from-[#1A4D2E] via-[#0d3a20] to-[#1A4D2E] overflow-hidden">
        {/* Decorative grain / pattern overlay */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 80% 80%, white 1px, transparent 1px)',
          backgroundSize: '50px 50px, 70px 70px',
        }} />
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#E07A5F] text-white px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase mb-5 shadow-lg">
              <Sparkles className="w-4 h-4" />
              Tijdelijke Actie
            </div>
            <h2 className="font-heading text-4xl md:text-6xl font-bold text-white tracking-tight mb-4">
              <span className="block">Stop met betalen aan</span>
              <span className="block">platforms die <span className="text-[#E07A5F]">15–30% pakken</span></span>
            </h2>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Met FoodFlows verkoop je via je <strong>eigen webshop</strong> aan{' '}
              <span className="bg-white/20 px-2 py-0.5 rounded font-bold text-white inline-flex items-center gap-1">
                <Percent className="w-5 h-5 inline" />0 commissie
              </span>{' '}
              per bestelling. Bespaar duizenden euro&apos;s per jaar.
            </p>
          </div>

          {/* Comparison cards */}
          <div className="grid md:grid-cols-3 gap-5 mb-12">
            {[
              { name: 'Uber Eats', range: '30%', loss: '€1.500/maand bij €5.000 omzet', icon: Ban, color: 'rose' },
              { name: 'Deliveroo', range: '25–35%', loss: '€1.250/maand bij €5.000 omzet', icon: Ban, color: 'rose' },
              { name: 'Takeaway / Just Eat', range: '14–30%', loss: '€700/maand bij €5.000 omzet', icon: Ban, color: 'rose' },
            ].map((p) => (
              <div key={p.name} className="bg-red-950/40 border border-red-500/40 rounded-2xl p-6 backdrop-blur" data-testid={`commission-card-${p.name.toLowerCase().replace(/[^a-z]/g,'-')}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center">
                    <p.icon className="w-5 h-5 text-red-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white">{p.name}</h3>
                </div>
                <p className="text-3xl font-bold text-red-300 mb-1">{p.range}</p>
                <p className="text-sm text-red-200/80">commissie per bestelling</p>
                <div className="mt-4 pt-4 border-t border-red-500/20">
                  <p className="text-xs uppercase tracking-wider text-red-300/70 mb-1">Verlies</p>
                  <p className="text-sm font-semibold text-white">{p.loss}</p>
                </div>
              </div>
            ))}
          </div>

          {/* FoodFlows card — highlighted */}
          <div className="bg-gradient-to-br from-[#E07A5F] to-[#c25a3f] rounded-3xl p-8 md:p-10 shadow-2xl mb-10" data-testid="foodflows-zero-comm-card">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-white/20 text-white px-3 py-1 rounded-full text-xs font-medium mb-4">
                  <Check className="w-4 h-4" />
                  FoodFlows
                </div>
                <h3 className="font-heading text-4xl md:text-5xl font-bold text-white mb-3">
                  €0 commissie
                </h3>
                <p className="text-lg text-white/90 mb-4">
                  Je betaalt alleen een vaste, lage maandprijs. Élke bestelling-euro is voor jou.
                </p>
                <ul className="space-y-2 text-white/95">
                  <li className="flex items-center gap-2"><Check className="w-5 h-5 flex-shrink-0" /> Geen commissie, geen verborgen kosten</li>
                  <li className="flex items-center gap-2"><Check className="w-5 h-5 flex-shrink-0" /> Je eigen klanten, je eigen data</li>
                  <li className="flex items-center gap-2"><Check className="w-5 h-5 flex-shrink-0" /> Geen prijsoorlog met collega&apos;s</li>
                  <li className="flex items-center gap-2"><Check className="w-5 h-5 flex-shrink-0" /> Vrij om eigen promoties te draaien</li>
                </ul>
              </div>
              <div className="bg-white/15 backdrop-blur rounded-2xl p-6 border border-white/30">
                <div className="text-center">
                  <p className="text-white/80 text-sm uppercase tracking-wider mb-2 flex items-center justify-center gap-2">
                    <PiggyBank className="w-5 h-5" />
                    Wat je bespaart per jaar
                  </p>
                  <div className="space-y-3 mt-4">
                    {[
                      { vol: '€3.000', save: '€7.200' },
                      { vol: '€5.000', save: '€12.000' },
                      { vol: '€10.000', save: '€24.000' },
                    ].map((row, i) => (
                      <div key={i} className="flex items-center justify-between bg-white/10 rounded-xl px-4 py-3" data-testid={`savings-row-${i}`}>
                        <span className="text-white/90 text-sm">Bij {row.vol}/maand omzet</span>
                        <span className="font-heading text-2xl font-bold text-white">{row.save}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-white/70 mt-4">
                    * Vergeleken met platforms die ~20% commissie rekenen, minus FoodFlows abonnement.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <a href="#demo-aanvragen">
              <Button className="bg-[#25D366] hover:bg-[#1eb855] text-white rounded-full h-14 px-8 text-base font-semibold shadow-2xl" data-testid="zero-comm-whatsapp-btn">
                <TrendingDown className="w-5 h-5 mr-2" />
                Stop met commissie betalen — vraag je demo aan
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
            <p className="text-white/70 text-sm mt-4">Gratis demo via WhatsApp · Antwoord binnen enkele minuten</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1F2937] tracking-tight mb-4">
              Alles Wat Je Nodig Hebt om te Slagen
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Een compleet platform om bestellingen, reserveringen te beheren en je restaurant te laten groeien.
            </p>
          </div>
          <div className="feature-grid">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="card-feature group animate-slideUp"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`feature-card-${index}`}
              >
                <div className="w-14 h-14 bg-[#D8F3DC] rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#1A4D2E] transition-colors">
                  <feature.icon className="w-7 h-7 text-[#1A4D2E] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-[#1F2937] mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Features Section */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1758523421981-6c9533e74550?crop=entropy&cs=srgb&fm=jpg&w=600"
                alt="Chef met tablet"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-8 -right-8 bg-white rounded-xl shadow-lg p-6 max-w-xs">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-[#E07A5F] rounded-full flex items-center justify-center">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-medium text-gray-900">AI Inzicht</span>
                </div>
                <p className="text-sm text-gray-600">
                  &ldquo;Verhoog pastaprijzen met 8% tijdens diner piek - vraag is 40% hoger.&rdquo;
                </p>
              </div>
            </div>
            <div>
              <div className="inline-flex items-center gap-2 bg-[#E07A5F]/10 text-[#E07A5F] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                AI-Aangedreven Intelligentie
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1F2937] tracking-tight mb-6">
                Laat AI het Zware Werk Doen
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Onze AI analyseert je bestellingen, voorspelt vraag en helpt je slimmere beslissingen te nemen om je bedrijf te laten groeien.
              </p>
              <ul className="space-y-4">
                {aiFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-[#D8F3DC] rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-[#1A4D2E]" />
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Demo Funnel Section */}
      <section id="demo-aanvragen" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-[#25D366]/10 text-[#128C7E] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <MessageCircle className="w-4 h-4" />
              Gratis & vrijblijvend
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1F2937] tracking-tight mb-4">
              Vraag je Gratis Demo aan via WhatsApp
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Vul je gegevens in en start direct een WhatsApp-gesprek. We laten je persoonlijk zien hoe jouw restaurant commissievrij online kan verkopen.
            </p>
          </div>
          <form onSubmit={handleLeadSubmit} className="bg-[#F8F9FA] border border-gray-200 rounded-3xl p-8 md:p-10 shadow-xl" data-testid="whatsapp-lead-form">
            <div className="grid md:grid-cols-2 gap-5 mb-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Jouw naam</label>
                <input
                  type="text"
                  required
                  value={leadName}
                  onChange={(e) => setLeadName(e.target.value)}
                  placeholder="bijv. Ahmed"
                  className="w-full h-12 px-4 rounded-xl border border-gray-300 focus:border-[#25D366] focus:ring-2 focus:ring-[#25D366]/30 outline-none bg-white"
                  data-testid="lead-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Naam restaurant</label>
                <input
                  type="text"
                  required
                  value={leadRestaurant}
                  onChange={(e) => setLeadRestaurant(e.target.value)}
                  placeholder="bijv. Pizzeria Roma"
                  className="w-full h-12 px-4 rounded-xl border border-gray-300 focus:border-[#25D366] focus:ring-2 focus:ring-[#25D366]/30 outline-none bg-white"
                  data-testid="lead-restaurant-input"
                />
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Plaats</label>
              <input
                type="text"
                required
                value={leadCity}
                onChange={(e) => setLeadCity(e.target.value)}
                placeholder="bijv. Antwerpen"
                className="w-full h-12 px-4 rounded-xl border border-gray-300 focus:border-[#25D366] focus:ring-2 focus:ring-[#25D366]/30 outline-none bg-white"
                data-testid="lead-city-input"
              />
            </div>
            <Button type="submit" className="w-full bg-[#25D366] hover:bg-[#1eb855] text-white rounded-full h-14 text-lg font-semibold shadow-lg" data-testid="lead-submit-btn">
              <MessageCircle className="w-5 h-5 mr-2" />
              Start WhatsApp-gesprek
            </Button>
            <p className="text-center text-xs text-gray-400 mt-4">
              Je wordt doorgestuurd naar WhatsApp — geen verplichtingen, antwoord meestal binnen enkele minuten.
            </p>
          </form>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1A4D2E]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight mb-6">
            Klaar om je Restaurant te Transformeren?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Sluit je aan bij restaurants die FoodFlows gebruiken om hun bedrijf te laten groeien. Vraag vandaag nog je gratis demo aan via WhatsApp.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#demo-aanvragen">
              <Button className="bg-[#25D366] hover:bg-[#1eb855] text-white rounded-full h-14 px-8 text-lg font-medium" data-testid="bottom-whatsapp-btn">
                <MessageCircle className="mr-2 w-5 h-5" />
                Vraag Gratis Demo Aan
              </Button>
            </a>
            <Link to="/shop/golden-fork-demo">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#1A4D2E] rounded-full h-14 px-8 text-lg">
                Bekijk Demo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Floating WhatsApp Button */}
      <a
        href={buildWhatsAppUrl(DEFAULT_WA_MESSAGE)}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] hover:bg-[#1eb855] rounded-full flex items-center justify-center shadow-2xl transition-transform hover:scale-110"
        data-testid="floating-whatsapp-btn"
        aria-label="Contact via WhatsApp"
      >
        <MessageCircle className="w-7 h-7 text-white" />
      </a>

      {/* Footer */}
      <footer className="bg-[#1F2937] text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-2 mb-6 md:mb-0">
              <div className="w-10 h-10 bg-[#1A4D2E] rounded-xl flex items-center justify-center">
                <Utensils className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-xl">FoodFlows</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <a href="#" className="hover:text-white transition-colors">Privacybeleid</a>
              <a href="#" className="hover:text-white transition-colors">Algemene Voorwaarden</a>
              <a href="#" className="hover:text-white transition-colors">Contact</a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-700 text-center text-sm text-gray-400">
            © 2025 FoodFlows. Alle rechten voorbehouden.
          </div>
        </div>
      </footer>
    </div>
  );
};
